import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import oneSpaceApi from '../lib/onespace-api';

// Background object structure from the API
interface BackgroundStyle {
  type: string; // "color", "image", "gradient"
  color: string;
  image?: string | null;
  gradient?: string | null;
}

// Defineer het thema object met alle mogelijk styling opties
interface ThemeColors {
  background: BackgroundStyle;
  text_color: string;
  primary_color: string;
  secondary_color: string;
  logo?: string | null;
}

// Legacy interface for backward compatibility
interface LegacyThemeColors {
  background_image?: string | null;
  background_color: string;
  text_color: string;
  primary_color: string;
  secondary_color: string;
  logo?: string | null;
}

interface ThemeStyleOptions {
  light: ThemeColors;
  dark: ThemeColors;
}

interface ThemeContextType {
  currentThemeColors: ThemeColors;
  setThemeStyles: (styles: ThemeStyleOptions) => void;
  applyThemeColors: () => void;
  fetchStylingFromAPI: (forceRefresh?: boolean, skipCacheCheck?: boolean) => Promise<void>;
}

// Default theme colors voor als er nog niets is ingesteld
const DEFAULT_THEME_STYLES: ThemeStyleOptions = {
  light: {
    background: {
      type: "color",
      color: "#ffffff",
      image: null,
      gradient: null
    },
    text_color: '#000000',
    primary_color: '#000000',
    secondary_color: '#ff9900'
  },
  dark: {
    background: {
      type: "color",
      color: "#000000",
      image: null,
      gradient: null
    },
    text_color: '#ffffff',
    primary_color: '#ffffff',
    secondary_color: '#ff9900'
  }
};

// Local storage key voor thema instellingen
const THEME_STYLES_KEY = 'onespace_theme_styles';
const THEME_STYLES_TIMESTAMP_KEY = 'onespace_theme_styles_timestamp';

// Create context met default values
const ThemeContext = createContext<ThemeContextType>({
  currentThemeColors: DEFAULT_THEME_STYLES.light,
  setThemeStyles: () => {},
  applyThemeColors: () => {},
  fetchStylingFromAPI: async () => {}
});

export const useCustomTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

// Helper function to convert legacy theme format to new format
const convertLegacyTheme = (legacyTheme: LegacyThemeColors): ThemeColors => {
  return {
    background: {
      type: legacyTheme.background_image ? "image" : "color",
      color: legacyTheme.background_color,
      image: legacyTheme.background_image || null,
      gradient: null
    },
    text_color: legacyTheme.text_color,
    primary_color: legacyTheme.primary_color,
    secondary_color: legacyTheme.secondary_color,
    logo: legacyTheme.logo
  };
};

// Helper function to check if an object is in legacy format
const isLegacyFormat = (obj: any): obj is LegacyThemeColors => {
  return obj && typeof obj.background_color === 'string' && !obj.background;
};

// Interne provider die de ThemeContext implementeert
const ThemeContextProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Gebruik next-themes voor light/dark mode functionaliteit
  const { theme, resolvedTheme } = useTheme();
  const [themeStyles, setThemeStyles] = useState<ThemeStyleOptions>(DEFAULT_THEME_STYLES);
  const [currentThemeColors, setCurrentThemeColors] = useState<ThemeColors>(DEFAULT_THEME_STYLES.light);
  const [isInitialized, setIsInitialized] = useState(false);

  // Laad opgeslagen thema stijlen uit localStorage bij initialisatie
  useEffect(() => {
    loadThemeFromLocalStorage();
    
    // Markeer als geïnitialiseerd nadat de eerste render is gedaan met localStorage data
    setIsInitialized(true);
    
    // Na initiele render, haal styling op van de API
    if (isInitialized) {
      fetchStylingFromAPI();
    }
  }, []);

  // Functie om thema uit localStorage te laden (snelle initiële render)
  const loadThemeFromLocalStorage = () => {
    try {
      const savedStyles = localStorage.getItem(THEME_STYLES_KEY);
      if (savedStyles) {
        let parsedStyles = JSON.parse(savedStyles);
        
        // Convert from legacy format if needed
        if (isLegacyFormat(parsedStyles.light)) {
          parsedStyles = {
            light: convertLegacyTheme(parsedStyles.light),
            dark: convertLegacyTheme(parsedStyles.dark)
          };
        }
        
        console.log('Loaded theme styles from localStorage:', parsedStyles);
        setThemeStyles(parsedStyles);
        
        // Direct de kleuren toepassen voor snelle weergave
        const currentMode = resolvedTheme === 'dark' ? 'dark' : 'light';
        setCurrentThemeColors(parsedStyles[currentMode]);
      }
    } catch (error) {
      console.error('Fout bij laden van thema stijlen uit localStorage:', error);
    }
  };

  // Functie om styling vanuit de API op te halen
  const fetchStylingFromAPI = async (forceRefresh = false, skipCacheCheck = false) => {
    // try {
    //   // Controleer of we recent al de stijlen hebben opgehaald (tenzij we forceren of skipCacheCheck)
    //   if (!forceRefresh && !skipCacheCheck) {
    //     const lastUpdate = localStorage.getItem(THEME_STYLES_TIMESTAMP_KEY);
    //     const now = Date.now();
        
    //     // Als we minder dan 5 minuten geleden hebben bijgewerkt, sla over
    //     if (lastUpdate && now - parseInt(lastUpdate) < 5 * 60 * 1000) {
    //       console.log('Recente styling update gevonden, API call overgeslagen');
    //       return;
    //     }
    //   }
      
    //   console.log('Fetching styling from API...');
      
    //   // Haal de styling op van de API
    //   const response = await oneSpaceApi.get('/api/settings/styling');
    //   // console.log('Received styling from API:', response.data);
      
    //   if (response.data && response.data.styling) {
    //     let apiStyles = response.data.styling;
        
    //     // Convert from legacy format if needed
    //     if (isLegacyFormat(apiStyles.light)) {
    //       apiStyles = {
    //         light: convertLegacyTheme(apiStyles.light),
    //         dark: convertLegacyTheme(apiStyles.dark)
    //       };
    //     }
        
    //     // Update de styling
    //     setThemeStyles(apiStyles);
        
    //     // Opslaan in localStorage
    //     localStorage.setItem(THEME_STYLES_KEY, JSON.stringify(apiStyles));
    //     localStorage.setItem(THEME_STYLES_TIMESTAMP_KEY, Date.now().toString());
        
    //     console.log('Updated theme styles from API');
        
    //     // Direct de kleuren toepassen
    //     const currentMode = resolvedTheme === 'dark' ? 'dark' : 'light';
    //     setCurrentThemeColors(apiStyles[currentMode]);
    //     applyThemeColors();
    //   } else {
    //     console.warn('API response did not contain styling data:', response.data);
    //   }
    // } catch (error) {
    //   console.error('Fout bij ophalen van styling vanuit API:', error);
    // }
  };

  // Update thema stijlen en sla op in localStorage
  const handleSetThemeStyles = (styles: ThemeStyleOptions) => {
    console.log('Setting theme styles:', styles);
    setThemeStyles(styles);
    
    // Sla op in localStorage voor persistentie
    try {
      localStorage.setItem(THEME_STYLES_KEY, JSON.stringify(styles));
      localStorage.setItem(THEME_STYLES_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Fout bij opslaan van thema stijlen in localStorage:', error);
    }
  };

  // Pas de juiste thema kleuren toe op basis van huidige light/dark mode
  const applyThemeColors = () => {
    // Bepaal of we light of dark mode gebruiken
    const currentMode = resolvedTheme === 'dark' ? 'dark' : 'light';
    console.log('Applying theme colors for mode:', currentMode);
    const colors = themeStyles[currentMode];
    
    setCurrentThemeColors(colors);

    // Functie om HEX naar RGB te converteren voor transparantie
    const hexToRgb = (hex: string) => {
      // Verwijder # indien aanwezig
      hex = hex.replace('#', '');
      
      // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
      if (hex.length === 3) {
        hex = hex.split('').map(h => h + h).join('');
      }
      
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return [r, g, b].join(', ');
    };

    // CSS variabelen updaten in :root element
    // Use the background.color for the background-color CSS variable
    document.documentElement.style.setProperty('--background-color', colors.background.color);
    document.documentElement.style.setProperty('--text-color', colors.text_color);
    document.documentElement.style.setProperty('--primary-color', colors.primary_color);
    document.documentElement.style.setProperty('--secondary-color', colors.secondary_color);
    
    // Set background image if available
    if (colors.background.image) {
      document.documentElement.style.setProperty('--background-image', `url(${colors.background.image})`);
    } else {
      document.documentElement.style.removeProperty('--background-image');
    }
    
    // RGB versies toevoegen voor transparantie ondersteuning
    if (colors.background.color.startsWith('#')) {
      document.documentElement.style.setProperty('--background-color-rgb', hexToRgb(colors.background.color));
    }
    if (colors.text_color.startsWith('#')) {
      document.documentElement.style.setProperty('--text-color-rgb', hexToRgb(colors.text_color));
    }
    if (colors.primary_color.startsWith('#')) {
      document.documentElement.style.setProperty('--primary-color-rgb', hexToRgb(colors.primary_color));
    }
    if (colors.secondary_color.startsWith('#')) {
      document.documentElement.style.setProperty('--secondary-color-rgb', hexToRgb(colors.secondary_color));
    }
    
    console.log('Applied theme variables:', {
      background: colors.background,
      text: colors.text_color,
      primary: colors.primary_color,
      secondary: colors.secondary_color
    });
  };

  // Update de kleuren wanneer het thema of theme stijlen wijzigen
  useEffect(() => {
    console.log('Theme changed:', { theme, resolvedTheme });
    applyThemeColors();
  }, [theme, resolvedTheme, themeStyles]);

  return (
    <ThemeContext.Provider 
      value={{ 
        currentThemeColors, 
        setThemeStyles: handleSetThemeStyles,
        applyThemeColors,
        fetchStylingFromAPI
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Externe provider die NextThemesProvider en ThemeContextProvider combineert
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <NextThemesProvider attribute="class" defaultTheme="system" enableSystem>
      <ThemeContextProvider>
        {children}
      </ThemeContextProvider>
    </NextThemesProvider>
  );
}; 